import { Link } from "react-router-dom";
import { PANNES_GEO } from "../../data/pannes-geo";

/**
 * Cross-link block that surfaces geo-localized panne pages (PANNES_GEO)
 * relevant to the current service / hub page.
 *
 * Props:
 *   - applianceServiceSlug  : exact match on PANNES_GEO[].applianceServiceSlug
 *                             (e.g. "reparateur-lave-linge-paris" from ServicePage)
 *   - cityHubLink           : exact match on PANNES_GEO[].cityHubLink
 *                             (e.g. "/reparateur-electromenager-paris" from ParisHubPage)
 *   - max                   : limit cards shown (default 6)
 *
 * Renders nothing when no match — safe to embed unconditionally.
 *
 * SEO goal: reach the 9 panne-geo pages currently orphaned (0 inbound
 * links) and pipe link equity into them. Each card uses anchor text that
 * matches the target H1 for keyword-rich internal linking.
 */
export default function RelatedGeoPannes({ applianceServiceSlug, cityHubLink, max = 6 }) {
    const items = PANNES_GEO.filter((p) => {
        if (applianceServiceSlug) return p.applianceServiceSlug === applianceServiceSlug;
        if (cityHubLink) return p.cityHubLink === cityHubLink;
        return false;
    }).slice(0, max);

    if (items.length === 0) return null;

    return (
        <section
            className="max-w-7xl mx-auto px-5 lg:px-10 py-12"
            aria-labelledby="related-geo-pannes-h2"
            data-testid="related-geo-pannes"
        >
            <div className="divider-amber mb-5" />
            <h2 id="related-geo-pannes-h2" className="heading text-2xl lg:text-3xl font-extrabold">
                Pannes fréquentes dans votre zone
            </h2>
            <p className="mt-2 text-[var(--ink-soft)] max-w-2xl">
                Symptômes les plus signalés près de chez vous — diagnostic à domicile sous 24 h.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((p) => (
                    <li key={p.slug}>
                        <Link
                            to={`/${p.slug}`}
                            className="card-soft p-5 block h-full hover:border-[var(--amber)] transition-colors"
                            data-testid={`geo-panne-link-${p.slug}`}
                        >
                            <div className="flex items-start gap-3">
                                <span className="w-10 h-10 rounded-lg bg-[var(--amber)]/15 grid place-items-center text-[var(--amber)] shrink-0">
                                    <i className={p.icon} />
                                </span>
                                <div className="min-w-0">
                                    <p className="heading text-base font-bold leading-tight text-[var(--navy)]">
                                        {p.h1.replace(/ — Intervention sous 24 h$/, "")}
                                    </p>
                                    <p className="text-xs text-[var(--ink-soft)] mt-1">
                                        Urgence&nbsp;: <strong>{p.urgence}</strong> · Coût&nbsp;{p.coutEstime}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}
